import React, {useContext, useEffect, useState} from "react";
import {useQuery} from "@apollo/client";
import {GET_FORM_QUERY} from "../Page/Page.query"
import Page from "../Page/Page";
import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import {makeStyles, createMuiTheme} from '@material-ui/core/styles';
import {CartContext} from "../../contexts/CartContext"
import {FormContext} from "../../contexts/FormContext"
import CheckoutPage from "../Checkout/Checkout";
import RecaptchaGate from "../Checkout/RecaptchaGate";
import {isCheckoutVerified} from "../../utils/checkoutVerification";
import PayrocHostedFields from "../Checkout/PayrocHostedFields";
import {ThemeProvider} from "@material-ui/styles";
import {StylesProvider} from '@material-ui/core/styles';
import {CardContent} from "@material-ui/core";
import {withRouter, Route, useParams, useHistory} from 'react-router-dom';
import {useForm} from "react-hook-form";
import DonationSetting from "../DonationSetting/DonationSetting";
import DonationSocialMediaShareBlock from "../DonationSocialMediaShareBlock/DonationSocialMediaShareBlock";
import * as ReactGA from "react-ga";
import TagManager from 'react-gtm-module';
import {buildProductConfirmationHTML} from "../../utils/productConfirmation";

const FormData = () => {
    const cartContext = useContext(CartContext);
    const venue = window.location.pathname.split("/")[1]
    const form = window.location.pathname.split("/")[2]
    const { page } = useParams();
    const history = useHistory();
    const [productSelectionError, setProductSelectionError] = useState(false);
    const [cpm, setCpm] = useState(null);
    const [autoCheckoutProcessed, setAutoCheckoutProcessed] = useState(false);
    const [personParamsSynced, setPersonParamsSynced] = useState(false);
    const [completedSubmissionData, setCompletedSubmissionData] = useState(null);

    const storeFormDataInLocalStorage = () => {
        const formDataToStore = {
            formData: formContext.formData,
            cart: {
                products: cartContext.products,
                donation: cartContext.donation,
                originalDonation: cartContext.originalDonation,
                total: cartContext.total,
                subtotal: cartContext.subtotal,
                transactionFee: cartContext.transactionFee,
                donationSettings: cartContext.donationSettings,
                custref: cartContext.custref
            },
            venue: venue,
            form: form,
            formId: formContext.form?.id,
            formName: formContext.form?.name,
            timestamp: new Date().toISOString()
        };
        localStorage.setItem('easyWareFormSubmission', JSON.stringify(formDataToStore));
        console.log('Form data stored in localStorage', formDataToStore);
    };

    const retrieveFormDataFromLocalStorage = () => {
        const storedData = localStorage.getItem('easyWareFormSubmission');
        if (storedData) {
            try {
                const parsedData = JSON.parse(storedData);
                if (parsedData.venue === venue && parsedData.form === form) {
                    return parsedData;
                }
            } catch (e) {
                console.error('Error parsing stored form data:', e);
            }
        }
        return null;
    };

    const clearFormDataFromLocalStorage = () => {
        localStorage.removeItem('easyWareFormSubmission');
        console.log('Form data cleared from localStorage');
    };

    const handlePartialFormSubmission = async () => {
        let custref = cartContext.custref
        if (!cartContext.custref) {
            custref = uuidv4()
        }
        await fetch(process.env.REACT_APP_SUBMIT_URL + "/submit/partial", {
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                ...formContext.formData,
                venue: venue,
                form: form,
                testMode: formContext.form.testMode,
                donationAmount: cartContext.donation,
                custref: custref,
                coveredCosts: (cartContext.donationSettings) ? cartContext.donationSettings.isSelected : false,
                donationSettings: cartContext.donationSettings,
                formId: formContext.formData.form.id,
                cpm: cpm,
                checkoutVerification: formContext.checkoutVerification?.token,
            }),
            method: 'POST',
        }).then(async (res) => {
                console.log(res)
                if (res.ok) {
                    // Store form data in localStorage before redirecting to payment
                    storeFormDataInLocalStorage();
                    
                    // Push form submission start event to GTM
                    if (data?.forms[0]?.venue?.googleTagManager) {
                        window.dataLayer = window.dataLayer || [];
                        window.dataLayer.push({
                            event: 'form_submission_started',
                            formName: formContext.form.name,
                            formId: formContext.form.id,
                            venue: venue,
                            formPath: form,
                            transactionId: custref,
                            transactionTotal: cartContext.total,
                            transactionSubtotal: cartContext.subtotal,
                            transactionFee: cartContext.transactionFee,
                            donationAmount: cartContext.donation?.price || 0,
                            donationCoveredCosts: cartContext.donationSettings?.isSelected || false,
                            products: Object.values(cartContext.products || {}).map(product => ({
                                id: product.id,
                                name: product.name,
                                price: product.price,
                                quantity: product.quantity,
                                eventId: product.eventId,
                                date: product.date
                            })),
                            customer: {
                                firstName: formContext.formData.patronFirstName || formContext.formData.buyersFirstName,
                                lastName: formContext.formData.patronLastName || formContext.formData.buyersLastName,
                                email: formContext.formData.patronEmail || formContext.formData.buyersEmail,
                                phone: formContext.formData.patronPhone || formContext.formData.buyersPhone,
                                address: formContext.formData.patronAddress || formContext.formData.buyersAddress,
                                city: formContext.formData.patronCity || formContext.formData.buyersCity,
                                region: formContext.formData.patronRegion || formContext.formData.buyersRegion,
                                country: formContext.formData.patronCountry || formContext.formData.buyersCountry,
                                zip: formContext.formData.patronZip || formContext.formData.buyersZip
                            }
                        });
                        console.log('GTM data layer push on form submission start');
                    }
                    
                    sendToTUNL()
                } else {
                    const failure = await res.json().catch(() => ({}));
                    if (failure.recaptchaRequired) formContext.setCheckoutVerification(null);
                    throw new Error(failure.error || "There was an error. Please try again later.");
                }
            }
        )
    }

    const formContext = useContext(FormContext);
    const methods = useForm();
    const {handleSubmit} = methods;

    const {loading, error, data} = useQuery(GET_FORM_QUERY, {
        fetchPolicy: "network-only",
        variables: {
            venue: venue,
            path: form
        },
    });

    const verification = formContext.checkoutVerification;
    const checkoutVerified = isCheckoutVerified(verification, data?.forms?.[0], cartContext.custref);
    const needsVerification = data?.forms?.[0]?.enableRecaptcha === true
        && data?.forms?.[0]?.venue?.paymentProcessor !== 'payroc_hosted_fields' && !checkoutVerified;
    useEffect(() => {
        if (data?.forms?.[0]?.enableRecaptcha && !cartContext.custref) cartContext.setCustRef(uuidv4());
    }, [data, cartContext.custref]);

    const handleChange = event => {
        formContext.setFormData({
            name: event.target.name,
            value: event.target.value,
        });

        // Detect recurring donation frequency from question block answers
        if (event.target.name && event.target.name.startsWith('question-block-') && event.target.value) {
            const answerLower = event.target.value.toString().toLowerCase();
            const isRecurringAnswer = (
                answerLower.includes('month') ||
                answerLower.includes('annual') ||
                answerLower.includes('quarter') ||
                answerLower.includes('weekly')
            );
            cartContext.setIsRecurring(isRecurringAnswer);
        }
    }

    const onSubmit = formData => {
        if (isLastPage() && formContext.isDataCollectionFormType()) {
            if (needsVerification) return;
            handlePartialFormSubmission().catch(error => alert(error.message))
            return
        }

        if (!data || !data.forms || !data.forms[0]) {
            console.error('Form data not yet loaded');
            return;
        }

        const currentPageIndex = currentPage();
        const currentFormPage = data.forms[0].pages[currentPageIndex];

        let proceedToNextPage = true;

        if (currentFormPage.productsRequired) {
            const findProductBlocks = (blocks) => {
                let productBlocks = [];
                blocks.forEach(block => {
                    if (block.__typename === 'ComponentBlocksProductBlock') {
                        productBlocks.push(block);
                    }
                    if (block.__typename === 'ComponentBlocksTwoColumnBlock') {
                        if (block.two_column_block) {
                            // Check left column
                            if (block.two_column_block.leftColumn) {
                                productBlocks = productBlocks.concat(findProductBlocks(block.two_column_block.leftColumn));
                            }
                            // Check right column
                            if (block.two_column_block.rightColumn) {
                                productBlocks = productBlocks.concat(findProductBlocks(block.two_column_block.rightColumn));
                            }
                        }
                    }
                });
                return productBlocks;
            };

            const productBlocks = findProductBlocks(currentFormPage.blocks);
            let productIdsOnPage = [];

            // Get product IDs from cart
            const productsInCart = cartContext.products ? Object.keys(cartContext.products).filter(id => id !== 'form-fee') : [];

            console.log("found product blocks:", productBlocks);
            console.log("products in cart", productsInCart);

            productBlocks.forEach((block) => {
                if (block.products_block && block.products_block.products) {
                    const blockProductIds = block.products_block.products.map((product) => product.id.toString());
                    productIdsOnPage = productIdsOnPage.concat(blockProductIds);
                }
            });

            console.log("productIdsOnPage", productIdsOnPage);

            const productsSelectedOnPage = productIdsOnPage.some((id) => {
                const isIncluded = productsInCart.includes(id);
                console.log(`Checking if ${id} is in cart: ${isIncluded}`);
                return isIncluded;
            });
            console.log("productsSelectedOnPage result:", productsSelectedOnPage);

            if (!productsSelectedOnPage) {
                setProductSelectionError(true);
                proceedToNextPage = false;
            } else {
                setProductSelectionError(false);
            }
        }

        if (!proceedToNextPage) {
            return
        }

        history.push(`/${venue}/${form}/${(parseInt(currentPage()) + 1)}`);
    }

    const theme = createMuiTheme({
        palette: {
            primary: {
                main: (data !== undefined) ? data.forms[0].venue.primaryColor : "#FF0000",
            },
            secondary: {
                main: (data !== undefined) ? data.forms[0].venue.secondaryColor : "#FF0000",
            },
        },
    });

    const useStyles = makeStyles((theme) => ({
        root: {
            margin: "auto",
            marginTop: 50,
            maxWidth: 1200,
        },
        backButton: {
            color: theme.palette.primary.light,
            position: "fixed"
        },
        nextButton: {
            marginTop: 20,
            marginBottom: 20
        },
        checkoutButton: {
            fontSize: "24px",
            right: 0,
            marginRight: 10,
            position: "absolute",
            textTransform: "none",
        },
        logo: {
            display: "block",
            marginLeft: "auto",
            marginRight: "auto",
            maxWidth: "-webkit-fill-available"
        },
        formName: {
            margin: "auto",
        },
        testModeBanner: {
            width: "100%",
            backgroundColor: "gray",
            margin: "auto",
            textAlign: "center",
            fontWeight: "bold"
        }
    }));

    const isLastPage = function () {
        return (parseInt(currentPage()) === data.forms[0].pages.length - 1);
    }

    const formHasMorePages = function () {
        return (parseInt(currentPage()) + 1 < data.forms[0].pages.length);
    }

    const isCheckoutPage = function () {
        return parseInt(currentPage()) === data.forms[0].pages.length;
    }

    const isSuccessPage = function () {
        return page === 'success'
    }

    const isFailurePage = function () {
        return page === 'failure'
    }

    const isPaymentPage = function () {
        return page === 'payment'
    }

    const currentPage = function () {
        return (page !== undefined) ? page : 0;
    }

    const hasCheckoutBlock = function () {
        return data.forms[0].pages[currentPage()].blocks.filter(function (item) {
                return item.__typename === "ComponentBlocksCheckoutBlock";
            }
        ).length > 0
    }

    const isBlankPage = function () {
        return data.forms[0].formType !== "BlankPage"
    }

    const classes = useStyles(theme);

    const addLocalCSS = function (css) {
        const style = document.createElement('style');
        style.textContent = css;
        document.head.appendChild(style);
    }

    const addRemoteCSS = function (css) {
        const style = document.createElement('link');
        style.href = css;
        style.rel = "stylesheet"
        document.head.appendChild(style);
    }

    const addCSSWithID = function (id, css) {
        const style = document.createElement('style');
        style.id = id;
        style.textContent = css;
        document.head.appendChild(style);
    }

    const removeCSSWithID = function (id) {
        let child = document.getElementById(id)

        if (child) {
            document.head.removeChild(child)
        }
    }

    function updateCSS() {
        addLocalCSS(data.forms[0].venue.css)
        addLocalCSS(data.forms[0].css)
        removeCSSWithID('page-css')

        if (isFormPage() && data.forms[0].pages[currentPage()].css) {
            addCSSWithID('page-css', data.forms[0].pages[currentPage()].css)
        }
    }

    function isFormPage() {
        return !isCheckoutPage() && !isSuccessPage() && !isFailurePage() && !isPaymentPage()
    }

    const redirectAfterSuccess = () => {
        setTimeout(() => {
            if (data.forms[0].redirectToURL) {
                window.location.href = data.forms[0].redirectToURL
            }
        }, 30000);
    }

    useEffect(() => {
        if (isSuccessPage()) {
            redirectAfterSuccess()

            // Push form data to GTM data layer
            const storedFormData = retrieveFormDataFromLocalStorage();
            if (storedFormData) {
                setCompletedSubmissionData(storedFormData);
            }
            if (storedFormData && data?.forms[0]?.venue?.googleTagManager) {
                // Prepare data for GTM
                const gtmData = {
                    event: 'form_submission_complete',
                    formName: storedFormData.formName || data.forms[0].name,
                    formId: storedFormData.formId || data.forms[0].id,
                    venue: storedFormData.venue,
                    formPath: storedFormData.form,
                    submissionTimestamp: storedFormData.timestamp,
                    
                    // Transaction data
                    transactionId: storedFormData.cart.custref,
                    transactionTotal: storedFormData.cart.total,
                    transactionSubtotal: storedFormData.cart.subtotal,
                    transactionFee: storedFormData.cart.transactionFee,
                    
                    // Donation data
                    donationAmount: storedFormData.cart.donation?.price || 0,
                    donationCoveredCosts: storedFormData.cart.donationSettings?.isSelected || false,
                    
                    // Products purchased
                    products: Object.values(storedFormData.cart.products || {}).map(product => ({
                        id: product.id,
                        name: product.name,
                        price: product.price,
                        quantity: product.quantity,
                        eventId: product.eventId,
                        date: product.date
                    })),
                    
                    // Customer information
                    customer: {
                        firstName: storedFormData.formData.patronFirstName || storedFormData.formData.buyersFirstName,
                        lastName: storedFormData.formData.patronLastName || storedFormData.formData.buyersLastName,
                        email: storedFormData.formData.patronEmail || storedFormData.formData.buyersEmail,
                        phone: storedFormData.formData.patronPhone || storedFormData.formData.buyersPhone,
                        address: storedFormData.formData.patronAddress || storedFormData.formData.buyersAddress,
                        city: storedFormData.formData.patronCity || storedFormData.formData.buyersCity,
                        region: storedFormData.formData.patronRegion || storedFormData.formData.buyersRegion,
                        country: storedFormData.formData.patronCountry || storedFormData.formData.buyersCountry,
                        zip: storedFormData.formData.patronZip || storedFormData.formData.buyersZip
                    },
                    
                    // All form fields (excluding sensitive data)
                    formFields: Object.keys(storedFormData.formData)
                        .filter(key => !key.toLowerCase().includes('password') && !key.toLowerCase().includes('cvv'))
                        .reduce((acc, key) => {
                            acc[key] = storedFormData.formData[key];
                            return acc;
                        }, {})
                };
                
                // Push to GTM data layer
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push(gtmData);
                console.log('GTM data layer push on confirmation:', gtmData);
            }

            // The captured data is only needed for this confirmation-page render.
            if (storedFormData) {
                clearFormDataFromLocalStorage();
            }
        }
    }, [data, page]); // Added page dependency to trigger on success page load

    useEffect(() => {
        if (!data) {
            return
        }
        ReactGA.initialize(data.forms[0].googleAnaltyicsId);
        ReactGA.pageview(window.location.pathname + window.location.search);
        console.log("GA pageview: " + data.forms[0].googleAnaltyicsId);

        // Initialize Google Tag Manager if ID is provided
        if (data.forms[0].venue.googleTagManager) {
            TagManager.initialize({ gtmId: data.forms[0].venue.googleTagManager });
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'pageview',
                page: window.location.pathname + window.location.search
            });
            console.log("GTM initialized: " + data.forms[0].venue.googleTagManager);
        }

        if (data?.forms?.[0]?.venue?.shippingConfig) {
            const sc = data.forms[0].venue.shippingConfig;
            const boxes = {};
            (sc.boxes || []).forEach((b) => { boxes[b.size] = { price: parseFloat(b.price), unitCapacity: b.unitCapacity }; });
            cartContext.setShippingConfig({
                enabled: sc.enabled,
                shippingItemID: sc.shippingItemID,
                mediumUnits: sc.mediumUnits,
                largeUnits: sc.largeUnits,
                boxes,
            });
        }
    }, [data])

    useEffect(() => {
        if (data && isCheckoutPage() && data.forms[0].skipShoppingCart && !cartContext.custref) {
            cartContext.setCustRef(uuidv4())
            return
        }
        if (data && isCheckoutPage() && data.forms[0].skipShoppingCart && !needsVerification) {
            handlePartialFormSubmission().catch(error => alert(error.message))
        }
    }, [data, page, cartContext.custref, needsVerification])

    useEffect(() => {
        if (!data) {
            return;
        }

        let donationSettings = data.forms[0].donationSettings
        if (donationSettings) {
            cartContext.setDonationSettings(donationSettings)
        }

        let formFees = data.forms[0].form_fees
        formFees.forEach((fee) => {
            if (fee.type === "amount" && fee.applied_per !== "transaction") {
                cartContext.updateProduct("form-fee", fee.label, 1, fee.amount, null, -1, "")
            } else {
                cartContext.setTransactionFee(fee)
            }
        })
    }, [data])

    function uuidv4() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    function post(path, params, method = 'post') {
        const form = document.createElement('form');
        form.method = method;
        form.action = path;

        for (const key in params) {
            if (params.hasOwnProperty(key)) {
                const hiddenField = document.createElement('input');
                hiddenField.type = 'hidden';
                hiddenField.name = key;
                hiddenField.value = params[key];

                form.appendChild(hiddenField);
            }
        }

        document.body.appendChild(form);
        form.submit();
    }

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentPage()])

    useEffect(() => {
        // Extract cpm parameter from URL query parameters
        const urlParams = new URLSearchParams(window.location.search);
        const cpmParam = urlParams.get('cpm');
        if (cpmParam) {
            setCpm(cpmParam);
            // Store cpm in form data
            handleChange({
                target: {
                    name: "cpm",
                    value: cpmParam,
                }
            });
        }
    }, [])

    // Sync person query params to form data (for auto-checkout scenarios where PersonBlock doesn't render)
    useEffect(() => {
        const personParams = formContext.personQueryParams || {};

        // If no person params, mark as synced immediately
        if (Object.keys(personParams).length === 0) {
            setPersonParamsSynced(true);
            return;
        }

        // Map of query param keys to form data field names
        const paramToFormFieldMap = {
            firstName: 'patronFirstName',
            lastName: 'patronLastName',
            email: 'patronEmail',
            homePhone: 'patronPhone',
            mobilePhone: 'patronPhone',
            workPhone: 'patronPhone',
            customPhone: 'patronPhone',
            address: 'patronAddress',
            address2: 'patronAddress2',
            city: 'patronCity',
            state: 'patronRegion',
            zip: 'patronZip',
            country: 'patronCountry',
        };

        Object.keys(personParams).forEach(paramKey => {
            const formFieldName = paramToFormFieldMap[paramKey];
            if (formFieldName && personParams[paramKey]) {
                handleChange({
                    target: {
                        name: formFieldName,
                        value: personParams[paramKey]
                    }
                });
            }
        });

        // Also set firstName/lastName in context for checkout display
        if (personParams.firstName) {
            formContext.setFirstName(personParams.firstName);
        }
        if (personParams.lastName) {
            formContext.setLastName(personParams.lastName);
        }
        if (personParams.zip) {
            formContext.setZipcode(personParams.zip);
        }

        // Mark person params as synced
        setPersonParamsSynced(true);
    }, [formContext.personQueryParams])

    // Handle amount query parameter and auto-checkout
    useEffect(() => {
        // Wait for data to load and person params to be synced before proceeding
        if (!data || autoCheckoutProcessed || !personParamsSynced) return;

        const { amount, donationName, autoCheckout } = formContext.checkoutQueryParams || {};

        if (amount && amount > 0) {
            // Add the amount as a donation to the cart
            cartContext.updateDonation('query-param-amount', donationName, amount);

            // Mark as processed to prevent re-running
            setAutoCheckoutProcessed(true);

            // If autoCheckout is enabled, redirect to checkout page
            if (autoCheckout) {
                // Calculate the checkout page index (after all form pages)
                const checkoutPageIndex = data.forms[0].pages.length;
                history.push(`/${venue}/${form}/${checkoutPageIndex}`);
            }
        }
    }, [data, autoCheckoutProcessed, personParamsSynced, formContext.checkoutQueryParams])

    useEffect(() => {
        handleChange({
            target: {
                name: "products",
                value: cartContext.products,
            }
        });

        if (cartContext.transactionFee) {
            handleChange({
                target: {
                    name: "transactionFee",
                    value: cartContext.transactionFee,
                }
            });
        }

        handleChange({
            target: {
                name: "donationAmount",
                value: cartContext.donation,
            }
        })

        handleChange({
            target: {
                name: "transactionTotal",
                value: cartContext.calculateTotal(),
            }
        });

        handleChange({
            target: {
                name: "subtotal",
                value: cartContext.calculateSubtotal(),
            }
        });

        handleChange({
            target: {
                name: "shippingAmount",
                value: cartContext.shipping,
            }
        });
    }, [cartContext]);

    const successURL = () => {
        return process.env.REACT_APP_SUBMIT_URL + "/" + venue + "/" + form + "/success"
    }

    const webhookURL = () => {
        return process.env.REACT_APP_SUBMIT_URL + "/webhook/success"
    }

    const failureURL = () => {
        return process.env.REACT_APP_SUBMIT_URL + "/" + venue + "/" + form + "/failure"
    }

    function sendToTUNL() {
        if (formContext.isDataCollectionFormType()) {
            history.push("/" + venue + "/" + form + "/success")
            return;
        }

        if (isCheckoutPage() && data.forms[0].skipShoppingCart) {
            let amount = cartContext.calculateTotal();
            if (amount === 0) {
                alert("Please select a product or make a donation to continue. ")
                return false
            }
            if (formContext.form?.venue?.paymentProcessor === 'payroc_hosted_fields') {
                history.push(`/${venue}/${form}/payment`);
                return;
            }
            post(formContext.tunlUrl(), {
                amount: amount,
                cardholdername: formContext.firstName + " " + formContext.lastName,
                street: formContext.currentBuyer()["address"],
                city: formContext.currentBuyer()["city"],
                state: formContext.currentBuyer()["region"],
                zip: formContext.currentBuyer()["zip"],
                country: formContext.currentBuyer()["country"],
                customer_email: formContext.currentBuyer()["email"],
                comments: form,
                custref: cartContext.custref,
                success_url: successURL(),
                webhook_url: webhookURL(),
                cancel_url: failureURL(),
                webhook_error_email: formContext.form.venue.webhookErrorEmail,
                hide_comments: (formContext.form.venue.hideComments === true) ? "1" : "0",
                hide_customer_email: (formContext.form.venue.hideCustomerEmail === true) ? "1" : "0",
                cpm: cpm,
            })
            return <div/>;
        }
    }

    function findDonationBlock() {
        let text = "";
        data.forms[0].pages.forEach((page) => {
            text = page.blocks.filter((block) => block.__typename === "ComponentBlocksDonationSocialMediaShareBlock")[0]
        })

        return text
    }

    if (data) {
        formContext.setForm(data.forms[0])

        const storedSubmissionData = completedSubmissionData
            || (isSuccessPage() ? retrieveFormDataFromLocalStorage() : null);
        const successHTML = isSuccessPage()
            ? buildProductConfirmationHTML(
                data.forms[0].successHTML,
                data.forms[0].pages,
                storedSubmissionData?.cart?.products
            )
            : data.forms[0].successHTML;

        updateCSS();

        if (data.forms[0].venue.remote_stylesheets) {
            data.forms[0].venue.remote_stylesheets.map(stylesheet => addRemoteCSS(stylesheet.url))
        }

        // A direct payment URL must pass through the same inline checkout verification.
        if (needsVerification && isPaymentPage()) {
            history.replace(`/${venue}/${form}/${data.forms[0].pages.length}`);
            return <CircularProgress/>;
        }

        const skipCheckout = formContext.checkoutQueryParams?.skipCheckout;

        if (isCheckoutPage() && formContext.firstName === undefined && !skipCheckout) {
            history.push("/" + venue + "/" + form + "/" + (currentPage() - 1))
            return <div/>
        }

        if (isCheckoutPage() && data.forms[0].skipShoppingCart && !needsVerification) {
            return (
                <CircularProgress/>
            )
        }

        // Standalone Payroc payment page — rendered outside the normal form wrapper
        if (isPaymentPage()) {
            return (
                <StylesProvider injectFirst>
                    {data.forms[0].testMode &&
                    <div id="testModeBanner" className={classes.testModeBanner}>
                        This form is in test mode.
                    </div>
                    }
                    <ThemeProvider theme={theme}>
                        <div style={{maxWidth: 480, margin: '0 auto', padding: '0 5px'}}>
                            <PayrocHostedFields
                                handleFormSubmission={handlePartialFormSubmission}
                                sendGoogleAnalytics={() => {
                                    if (data.forms[0].googleAnaltyicsId) {
                                        ReactGA.initialize(data.forms[0].googleAnaltyicsId);
                                        ReactGA.plugin.require('ecommerce');
                                        Object.keys(cartContext.products).forEach(key => (
                                            ReactGA.plugin.execute('ecommerce', 'addProduct', {
                                                id: key,
                                                name: cartContext.products[key].name,
                                                sku: key,
                                                category: '',
                                                price: cartContext.products[key].price.toFixed(2),
                                                quantity: cartContext.products[key].quantity,
                                            })
                                        ));
                                        ReactGA.plugin.execute('ec', 'setAction', 'purchase', {
                                            id: cartContext.custref,
                                            revenue: cartContext.total,
                                        });
                                        ReactGA.plugin.execute('ecommerce', 'send');
                                        ReactGA.plugin.execute('ecommerce', 'clear');
                                    }
                                }}
                            />
                        </div>
                    </ThemeProvider>
                </StylesProvider>
            );
        }

        return (
            <StylesProvider injectFirst>
                {data.forms[0].testMode &&
                <div id="testModeBanner" className={classes.testModeBanner}>
                    This form is in test mode.
                </div>
                }
                <ThemeProvider theme={theme}>
                    <div id="logo-container">
                        <a href={data.forms[0].venue.website}>
                            <img src={data.forms[0].venue.logo.url}
                                 id="logo"/>
                        </a>
                    </div>

                    <CardContent className={classes.root} id="card">
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Grid container justify="center">
                                <Grid item xs={12}>
                                    {isSuccessPage() && (
                                        <>
                                        <div dangerouslySetInnerHTML={{__html: successHTML}}/>

                                        {data.forms[0].showSocialMediaShareButtonsOnCheckout && (
                                            <DonationSocialMediaShareBlock sharedImage={findDonationBlock()?.sharedImage} sharedQuote={findDonationBlock()?.sharedQuote} headerText={findDonationBlock()?.headerText} />
                                        )}
                                        </>
                                    )}
                                    {isFailurePage() && (
                                        <div dangerouslySetInnerHTML={{__html: data.forms[0].failureHTML}}/>
                                    )}
                                    {isCheckoutPage() &&
                                    <CheckoutPage handleFormSubmission={handlePartialFormSubmission} form={methods} skipCheckout={skipCheckout}/>
                                    }
                                    {isFormPage() &&
                                    <Page key={data.forms[0].pages[currentPage()].id} form={methods}
                                          handleFormSubmission={handlePartialFormSubmission} handleChange={handleChange}
                                          page={data.forms[0].pages[currentPage()]}/>
                                    }
                                </Grid>
                            </Grid>

                            {productSelectionError && (
                              <div style={{ color: 'red', marginBottom: '1rem' }}>
                                Please select at least one product before proceeding.
                              </div>
                            )}

                            {formHasMorePages() &&
                            <Grid container justify="center">
                                <Button
                                  variant="contained"
                                  color="primary"
                                  id="next-page"
                                  type="submit"
                                >
                                  Next
                                </Button>
                            </Grid>
                            }

                            {(cartContext.donationSettings && !isSuccessPage() && cartContext.donationSettings.placement === "bottomOfForm") && (
                                <DonationSetting
                                    donationSetting={cartContext.donationSettings}
                                />
                            )}

                            {needsVerification && isLastPage() && !hasCheckoutBlock() && formContext.isDataCollectionFormType() &&
                                <RecaptchaGate formId={data.forms[0].id} custref={cartContext.custref}
                                    onVerified={formContext.setCheckoutVerification}/>
                            }
                            {isLastPage() && !hasCheckoutBlock() && isBlankPage() &&
                            <Grid container justify="center">
                                <Button className={classes.nextButton} size="small"
                                        color="secondary"
                                        id="checkout-button"
                                        disabled={formContext.isDataCollectionFormType() && needsVerification}
                                        type="submit">
                                    {formContext.isDataCollectionFormType() && (
                                        "Submit"
                                    )}
                                    {!formContext.isDataCollectionFormType() && (
                                        "Checkout"
                                    )}
                                </Button>
                            </Grid>
                            }
                        </form>
                    </CardContent>
                </ThemeProvider>
            </StylesProvider>
        )
    }

    return (
        <CircularProgress/>
    );
}

const Form = (props) => {
    return (
        <Route path={`/:venue/:form/:page?`} component={FormData}>
            <FormData venue={props.venue} form={props.form}/>
        </Route>
    )
};


export default withRouter(Form);
